import { observable } from "mobx";
import { types } from "mobx-state-tree";
const dashReplacement = "_D_A_S_H_";
function replaceDashesInId(id) {
    // Replace dashes with a unique string that is unlikely to appear in attribute IDs
    // This is necessary because mathjs does not allow dashes in variable names.
    // We use a unique string so that we can reverse the process later.
    return id.replace(/-/g, dashReplacement);
}
function restoreDashesInId(id) {
    // Reverse the dash replacement
    return id.replace(new RegExp(dashReplacement, "g"), "-");
}
function getFormulaAttribute(attr) {
    return {
        id: replaceDashesInId(attr.id),
        name: attr.name || "",
        title: attr.name || "",
        formula: attr.formula,
        hasFormula: attr.formula != null,
        // FIXME: need to validate the formula
        hasValidFormula: attr.formula != null,
        strValues: attr.strValues,
        numValue: attr.numValue
    };
}
const kDefaultCollectionId = "__default-collection__";
/**
 * FormulaDataSetProxy is a MobX State Tree (MST) model that acts as a proxy
 * for a CLUE IDataSet, allowing it to be used with the formula system.
 * It provides methods and views that mimic the expected interface of a formula data set.
 */
const FormulaDataSetProxy = types.model("FormulaDataSetProxy", {})
    .volatile(self => ({
    dataSet: {},
}))
    .actions(self => ({
    setCLUEDataSet(dataSet) {
        self.dataSet = dataSet;
    }
}))
    .views(self => ({
    get _itemIds() {
        var _a, _b;
        return (_b = (_a = self.dataSet.cases) === null || _a === void 0 ? void 0 : _a.map(({ __id__ }) => __id__)) !== null && _b !== void 0 ? _b : [];
    },
}))
    .views(self => ({
    get allICases() {
        return self.dataSet.getCases(self._itemIds);
    }
}))
    .views(self => ({
    get defaultCollection() {
        // CLUE doesn't support collections, so return a single ungrouped collection
        return {
            id: kDefaultCollectionId,
            attributes: self.dataSet.attributes.map(getFormulaAttribute),
            cases: self.allICases,
            // FIXME: what should caseGroups be?
            caseGroups: [],
            caseIds: self._itemIds,
        };
    }
}))
    .views(self => ({
    get id() {
        return self.dataSet.id;
    },
    get name() {
        return self.dataSet.name || "";
    },
    get title() {
        return self.dataSet.name || "";
    },
    get itemIdsHash() {
        var _a;
        // This is supposed to be a hash of the item IDs, it is used by the formula system
        // to watch the set of items, like when items are added or removed.
        // In CLUE we are also using it to watch the values of the attributes.
        // In CODAP the values are "watched" by monitoring calls to setCaseValues, but
        // because this proxy is only used by the formula library, the setCaseValues action
        // here will not be called by CLUE.
        // This approach of updating a hash based on the values is in-efficient,
        // but in CLUE the number of items and attributes is relatively small.
        const stringsOfDataSet = [];
        const caseIds = [];
        (_a = self.dataSet.cases) === null || _a === void 0 ? void 0 : _a.forEach(({ __id__ }) => {
            stringsOfDataSet.push(__id__);
            caseIds.push(__id__);
        });
        // Key per-value entries by case id (not index) so that reordering the
        // cases (e.g. via DataSet.sortCases) doesn't change the string set and
        // wrongly trigger formula re-evaluation, which would call back into
        // setCanonicalCaseValues and reset sort state.
        self.dataSet.attributes.forEach(attr => {
            attr.strValues.forEach((value, index) => {
                stringsOfDataSet.push(`${attr.id}-${caseIds[index]}-${value}`);
            });
        });
        return hashStringSet(stringsOfDataSet);
    },
    get attrNameMap() {
        const attrNameMap = observable.map({}, { name: "attrNameMap" });
        self.dataSet.attributes.forEach(attr => {
            attrNameMap.set(attr.name, replaceDashesInId(attr.id));
        });
        return attrNameMap;
    },
    get collections() {
        // CLUE doesn't support collections, so return a single-element array
        return [self.defaultCollection];
    },
    get attributes() {
        return self.dataSet.attributes.map(getFormulaAttribute);
    },
    getValue(caseID, attributeID) {
        return self.dataSet.getValue(caseID, restoreDashesInId(attributeID));
    },
    attrFromID(attrId) {
        const attr = self.dataSet.attrFromID(restoreDashesInId(attrId));
        return attr ? getFormulaAttribute(attr) : undefined;
    },
    validateCases() {
        // The main purpose of validateCases() is to group cases in hierarchical data sets.
        // Since CLUE doesn't support hierarchy, doing nothing may be fine.
        // Just to be safe, we log a warning incase a formula triggers this call.
        console.warn("validateCases is not implemented in formula data set proxy");
        return undefined;
    },
    getAttribute(id) {
        const attr = self.dataSet.attrFromID(restoreDashesInId(id));
        return attr ? getFormulaAttribute(attr) : undefined;
    },
    getCollectionIndex(collectionId) {
        if (collectionId === kDefaultCollectionId) {
            // CLUE doesn't support collections, so default collection is at index 0
            return 0;
        }
        // Anything else is invalid.
        console.warn(`getCollectionIndex: collectionId ${collectionId} is not valid`);
        return -1;
    },
    getCollectionForAttribute(attributeId) {
        // CLUE doesn't support collections, so return the default collection
        return self.defaultCollection;
    },
    getCollectionForCase(caseId) {
        // CLUE doesn't support collections, so return the default collection
        return self.defaultCollection;
    },
    get caseInfoMap() {
        // CHECKME: do we need to return something here?
        // This is primarily used for grouping in hierarchical data sets, so this might be fine.
        return new Map();
    },
    getItemIndex(itemId) {
        return self._itemIds.indexOf(itemId);
    },
    // CHECKME: this might need to return numeric values when
    // that is what CODAP would do
    getValueAtItemIndex(index, attributeID) {
        const attr = self.dataSet.attrFromID(restoreDashesInId(attributeID));
        if (attr) {
            return attr.strValues[index];
        }
        return undefined;
    },
    get items() {
        return self._itemIds.map(id => ({ __id__: id }));
    },
    get hasFilterFormula() {
        return false;
    },
    get itemsNotSetAside() {
        // CODAP has a concept of hidden items, CLUE does not.
        return self._itemIds;
    },
    getItem(itemId) {
        console.warn("getItem is not implemented in formula data set proxy");
        return undefined;
    },
    get filterFormula() {
        // CLUE doesn't support filter formulas, so always return undefined
        return undefined;
    },
    getAttributeByName(name) {
        const attr = self.dataSet.attrFromName(name) || undefined;
        return attr ? getFormulaAttribute(attr) : undefined;
    },
    attrIDFromName(name) {
        const attr = self.dataSet.attrFromName(name);
        return attr ? replaceDashesInId(attr.id) : undefined;
    }
}))
    .views(self => ({
    getCollectionForAttributes(attributeIds) {
        self.validateCases();
        return self.defaultCollection;
    },
}))
    .views(self => ({
    getCasesForAttributes(attributeIds) {
        // Because CLUE doesn't support collections, we just return all of the cases
        return self.allICases;
    },
}))
    .actions(self => ({
    setCaseValues(cases) {
        // Replace dashes in the attribute IDs
        const transformedCases = cases.map((c) => {
            // Create a new case object with transformed attribute IDs
            const newCase = { __id__: c.__id__ };
            Object.keys(c).forEach((key) => {
                newCase[restoreDashesInId(key)] = c[key];
            });
            return newCase;
        });
        // FIXME: the formula system works with strings, numbers, booleans, dates, and undefined values
        // but CLUE only supports strings, numbers, and undefined values.
        // We should sanitize the cases to ensure they are valid for CLUE.
        self.dataSet.setCanonicalCaseValues(transformedCases);
    },
    updateFilterFormulaResults() {
        // This is a no-op in CLUE, and it shouldn't be called so
        // log a warning if it is
        console.warn("updateFilterFormulaResults is not implemented in formula data set proxy");
    },
    setFilterFormulaError(error) {
        // This is a no-op in CLUE, and it shouldn't be called so
        // log a warning if it is
        console.warn("setFilterFormulaError is not implemented in formula data set proxy");
    },
}));
/**
 * Create a formula data set proxy for a given CLUE IDataSet.
 * This function wraps the CLUE IDataSet in a FormulaDataSetProxy,
 * allowing it to be used with the formula system.
 *
 * It is best to reuse an existing FormulaDataSetProxy if one exists.
 * That can be done by looking at the dataSets property of the formula manager
 *
 * @param dataSet
 * @returns
 */
export function createFormulaDataSetProxy(dataSet) {
    const formulaDataSet = FormulaDataSetProxy.create();
    formulaDataSet.setCLUEDataSet(dataSet);
    return formulaDataSet;
}
/*
 * hashString()
 *
 * Returns a 32-bit hash value for a string.
 * Provided by ChatGPT, but apparently originally developed by Daniel J. Bernstein.
 */
export function hashString(str) {
    // Simple hash function for a single string (e.g., DJB2)
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        // eslint-disable-next-line no-bitwise
        hash = (hash * 33) ^ str.charCodeAt(i);
    }
    // eslint-disable-next-line no-bitwise
    return hash >>> 0; // Convert to unsigned 32-bit integer
}
/*
 * hashStringSet()
 *
 * returns an order-invariant hash value for a set of strings (e.g. ids).
 * developed with the help of ChatGPT.
 */
export function hashStringSet(strings) {
    return strings
        .map(hashString)
        // eslint-disable-next-line no-bitwise
        .reduce((acc, hash) => acc ^ hash, 0); // XOR all individual hashes
}
//# sourceMappingURL=data:application/json;base64,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